/**
 * Unified portfolio period change (ported from mobile app).
 * @see apps/mobile/services/portfolioPeriodChange.js
 */

export function ymdLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function getPortfolioTimeframeRangeStartStr(timeFrame) {
  const today = new Date();
  switch (timeFrame) {
    case "1d":
      return ymdLocal(today);
    case "5d":
    case "1w": {
      const x = new Date(today);
      x.setDate(x.getDate() - 7);
      return ymdLocal(x);
    }
    case "1mo": {
      const x = new Date(today);
      x.setMonth(x.getMonth() - 1);
      return ymdLocal(x);
    }
    case "3mo": {
      const x = new Date(today);
      x.setMonth(x.getMonth() - 3);
      return ymdLocal(x);
    }
    case "6mo": {
      const x = new Date(today);
      x.setMonth(x.getMonth() - 6);
      return ymdLocal(x);
    }
    case "1y": {
      const x = new Date(today);
      x.setFullYear(x.getFullYear() - 1);
      return ymdLocal(x);
    }
    case "5y":
    case "max": {
      const x = new Date(today);
      x.setFullYear(x.getFullYear() - 5);
      return ymdLocal(x);
    }
    case "ytd":
      return `${today.getFullYear()}-01-01`;
    default:
      return null;
  }
}

export function historicalCloseAtPeriodStart(history, rangeStartStr, timeFrame) {
  if (!Array.isArray(history) || history.length === 0) return null;
  const undatedPrices = history
    .map((row) => Number(row?.price))
    .filter((price) => Number.isFinite(price) && price > 0);
  const rows = history
    .map((row) => {
      const price = Number(row?.price);
      if (!Number.isFinite(price) || price <= 0) return null;
      const raw = row?.date;
      if (raw == null || raw === "") return null;
      const t = raw instanceof Date ? raw.getTime() : new Date(raw).getTime();
      if (!Number.isFinite(t) || Number.isNaN(t)) return null;
      return { t, price, ymd: ymdLocal(new Date(t)) };
    })
    .filter(Boolean);

  if (timeFrame === "1d") {
    if (rows.length > 0) {
      rows.sort((a, b) => a.t - b.t);
      return rows[0].price;
    }
    if (undatedPrices.length > 0) return undatedPrices[0];
    return null;
  }

  if (rows.length === 0) {
    if (undatedPrices.length > 0) return undatedPrices[0];
    return null;
  }

  rows.sort((a, b) => a.t - b.t);

  if (!rangeStartStr) {
    return rows[0].price;
  }
  for (const r of rows) {
    if (r.ymd >= rangeStartStr) return r.price;
  }
  return rows[0].price;
}

export function txPricePerShareDisplay(tx) {
  const shares = Number(tx?.shares) || 0;
  if (shares <= 0) return 0;
  const total = Number(
    tx?.totalAmountBase ?? tx?.totalAmountPortfolio ?? tx?.totalAmount
  );
  if (Number.isFinite(total) && total > 0) return total / shares;
  const p = Number(tx?.price);
  return Number.isFinite(p) && p > 0 ? p : 0;
}

function symbolKey(symbol) {
  return String(symbol || "").trim().toUpperCase();
}

function sortedSymbolTransactions(symbol, transactions, convertTimestamp) {
  const key = symbolKey(symbol);
  return [...(transactions || [])]
    .filter((t) => symbolKey(t?.symbol) === key)
    .map((t) => ({ tx: t, date: convertTimestamp(t?.date) }))
    .filter(({ date }) => date && !Number.isNaN(date.getTime()))
    .sort((a, b) => a.date - b.date)
    .map(({ tx, date }) => ({ ...tx, _date: date }));
}

export function collectSymbolsForPeriodChange(
  ownedStocks,
  transactions,
  timeFrame,
  convertTimestamp
) {
  const symbols = new Set();
  for (const stock of ownedStocks || []) {
    const sym = symbolKey(stock?.symbol);
    if (sym) symbols.add(sym);
  }
  const rangeStartStr = getPortfolioTimeframeRangeStartStr(timeFrame);
  for (const tx of transactions || []) {
    const type = String(tx?.type || "").toLowerCase();
    if (type !== "buy" && type !== "sell") continue;
    const sym = symbolKey(tx?.symbol);
    if (!sym) continue;
    const d = convertTimestamp(tx?.date);
    if (!d || Number.isNaN(d.getTime())) continue;
    if (rangeStartStr && ymdLocal(d) < rangeStartStr) continue;
    symbols.add(sym);
  }
  return [...symbols];
}

export function computeSymbolPeriodChangeDisplay({
  symbol,
  transactions,
  periodStartPrice,
  periodEndPrice,
  timeFrame,
  convertTimestamp,
  asOfYmd = null,
}) {
  const P_start = Number(periodStartPrice);
  if (!Number.isFinite(P_start) || P_start <= 0) {
    return { currencyChange: 0, percentageChange: 0, baseline: 0 };
  }
  const P_end =
    Number.isFinite(Number(periodEndPrice)) && Number(periodEndPrice) > 0
      ? Number(periodEndPrice)
      : null;

  const rangeStartStr = getPortfolioTimeframeRangeStartStr(timeFrame);
  const txs = sortedSymbolTransactions(symbol, transactions, convertTimestamp);

  let sharesAtStart = 0;
  for (const tx of txs) {
    const txYmd = ymdLocal(tx._date);
    if (rangeStartStr && txYmd >= rangeStartStr) break;
    if (asOfYmd && txYmd > asOfYmd) break;
    const type = String(tx?.type || "").toLowerCase();
    const q = Number(tx?.shares) || 0;
    if (q <= 0) continue;
    if (type === "buy") sharesAtStart += q;
    else if (type === "sell") sharesAtStart -= q;
  }

  const periodTxs = txs.filter((tx) => {
    const txYmd = ymdLocal(tx._date);
    if (rangeStartStr && txYmd < rangeStartStr) return false;
    if (asOfYmd && txYmd > asOfYmd) return false;
    return true;
  });

  let change = 0;
  let sharesSoldInPeriod = 0;

  for (const tx of periodTxs) {
    if (String(tx?.type || "").toLowerCase() !== "sell") continue;
    const q = Number(tx?.shares) || 0;
    if (q <= 0) continue;
    const sellPx = txPricePerShareDisplay(tx);
    if (sellPx <= 0) continue;
    change += q * (sellPx - P_start);
    sharesSoldInPeriod += q;
  }

  const sharesContinuous = Math.max(0, sharesAtStart - sharesSoldInPeriod);
  if (sharesContinuous > 0 && P_end != null) {
    change += sharesContinuous * (P_end - P_start);
  }

  let baseline = sharesAtStart > 0 ? sharesAtStart * P_start : 0;

  for (const tx of periodTxs) {
    if (String(tx?.type || "").toLowerCase() !== "buy") continue;
    const q = Number(tx?.shares) || 0;
    if (q <= 0 || P_end == null) continue;
    const buyPx = txPricePerShareDisplay(tx);
    if (buyPx <= 0) continue;
    change += q * (P_end - buyPx);
    baseline += q * buyPx;
  }

  const percentageChange = baseline > 0 ? (change / baseline) * 100 : 0;
  return { currencyChange: change, percentageChange, baseline };
}

export function computePortfolioPeriodChangeDisplay({
  ownedStocks,
  transactions,
  timeFrame,
  convertTimestamp,
  periodStartPriceBySymbol = {},
  periodEndPriceBySymbol = {},
}) {
  const symbols = collectSymbolsForPeriodChange(
    ownedStocks,
    transactions,
    timeFrame,
    convertTimestamp
  );

  let currencyChange = 0;
  let baseline = 0;

  for (const sym of symbols) {
    const result = computeSymbolPeriodChangeDisplay({
      symbol: sym,
      transactions,
      periodStartPrice: periodStartPriceBySymbol[sym],
      periodEndPrice: periodEndPriceBySymbol[sym],
      timeFrame,
      convertTimestamp,
    });
    currencyChange += result.currencyChange;
    baseline += result.baseline;
  }

  const percentageChange = baseline > 0 ? (currencyChange / baseline) * 100 : 0;
  return { currencyChange, percentageChange, baseline };
}

export function periodChangeDisplayToUsdInternal(displayChange, exchangeRateFromUSD) {
  const rate = Number(exchangeRateFromUSD);
  if (!Number.isFinite(rate) || rate <= 0) return displayChange;
  return displayChange / rate;
}

function symKey(symbol) {
  return symbolKey(symbol);
}

export function historicalCloseAtOrBeforeDate(
  historicalData,
  dateKey,
  usesTimestampChartPoints
) {
  if (!Array.isArray(historicalData) || historicalData.length === 0) return null;
  const rows = historicalData
    .filter((point) => {
      const px = Number(point?.price);
      if (!Number.isFinite(px) || px <= 0) return false;
      if (usesTimestampChartPoints) {
        return String(point.date) <= String(dateKey);
      }
      return String(point.date).split("T")[0] <= String(dateKey).split("T")[0];
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  if (rows.length === 0) return null;
  return Number(rows[0].price);
}

export function buildPortfolioPeriodChangeSeries({
  ownedStocks,
  investmentsWithHistory,
  transactions,
  timeFrame,
  convertTimestamp,
  exchangeRateFromUSD,
  sortedDates,
  usesTimestampChartPoints,
  periodStartPriceBySymbol = {},
}) {
  const fx = Number(exchangeRateFromUSD) > 0 ? Number(exchangeRateFromUSD) : 1;

  const symbols = collectSymbolsForPeriodChange(
    ownedStocks,
    transactions,
    timeFrame,
    convertTimestamp
  );

  const histDisplayBySymbol = {};
  for (const sym of symbols) {
    const inv = investmentsWithHistory?.find(
      (row) => symKey(row?.symbol) === sym
    );
    histDisplayBySymbol[sym] = (inv?.historicalData || []).map((point) => ({
      date: point.date,
      price: Number(point.price) * fx,
    }));
  }

  const portfolioHistory = [];

  for (const dateKey of sortedDates) {
    const asOfYmd = usesTimestampChartPoints
      ? ymdLocal(new Date(dateKey))
      : String(dateKey).split("T")[0];

    const periodEndDisplayBySymbol = {};
    for (const sym of symbols) {
      const px = historicalCloseAtOrBeforeDate(
        histDisplayBySymbol[sym],
        dateKey,
        usesTimestampChartPoints
      );
      if (Number.isFinite(px) && px > 0) {
        periodEndDisplayBySymbol[sym] = px;
      }
    }

    let currencyChangeDisplay = 0;
    let baselineDisplay = 0;

    for (const sym of symbols) {
      const result = computeSymbolPeriodChangeDisplay({
        symbol: sym,
        transactions,
        periodStartPrice: periodStartPriceBySymbol[sym],
        periodEndPrice: periodEndDisplayBySymbol[sym],
        timeFrame,
        convertTimestamp,
        asOfYmd,
      });
      currencyChangeDisplay += result.currencyChange;
      baselineDisplay += result.baseline;
    }

    const priceUsdInternal = periodChangeDisplayToUsdInternal(
      currencyChangeDisplay,
      fx
    );
    const pct =
      baselineDisplay > 0 ? (currencyChangeDisplay / baselineDisplay) * 100 : 0;

    portfolioHistory.push({
      date: dateKey,
      price: priceUsdInternal,
      totalValue: priceUsdInternal,
      change: pct,
    });
  }

  return portfolioHistory;
}
